// Minimal changes for Vite compatibility

import { useLocation } from 'react-router-dom'
import { LocalizedLink } from './localized-link.component'
import { twMerge } from 'src/utils/tw-merge.util'
import { BottomCurve, TopCurve } from './sidebar-item-outside-curve.component'
import type { TProjectFeatureName } from '../../types/project'
import { useRef } from 'react'
import { WithFeatureEnabled, WithPermission } from 'src/providers/current-project.provider'
import type { EPermissionGrantActions, EPermissionGrantSubjects } from 'src/pages/configuration/permissions/_components/permissions.type'

type TRequiredPermission = {
  subject: EPermissionGrantSubjects
  action: EPermissionGrantActions
}

export type TMenuTab = {
  path: string
  label: string
  IconComponent: (props: any) => React.JSX.Element
  requiredFeature?: TProjectFeatureName[]
  requiredPermission?: TRequiredPermission
  subItems?: TMenuTab[]
}

const TabContent = ({
  tab,
  isCollapsed,
  activeLinkLabel,
  isLabelHidden,
  isSubItem = false,
}: {
  tab: TMenuTab
  isCollapsed: boolean
  activeLinkLabel?: string
  isLabelHidden?: boolean
  isSubItem?: boolean
}) => {
  return (
    <LocalizedLink
      className={twMerge(
        'c-px-4 c-py-3 c-text-primary-1000 c-flex c-items-center group c-relative c-w-full',
        isSubItem && 'c-px-3 c-py-2',
        activeLinkLabel === tab.label &&
          'c-bg-white c-rounded-tl-full c-rounded-bl-full c-ml-2 c-w-[calc(100%-0.5rem)]',
        isLabelHidden && 'c-justify-center',
      )}
      to={tab.path}
    >
      {activeLinkLabel === tab.label && (
        <>
          <div
            className={`${
              isCollapsed ? 'c-top-[-10px]' : 'c-top-[-30px]'
            } c-absolute c-right-0  c-pointer-events-none`}
          >
            <TopCurve size={isCollapsed ? 'SMALL' : 'BIG'} />
          </div>
          <div
            className={`${
              isCollapsed ? 'c-bottom-[-10px]' : 'c-bottom-[-30px]'
            } c-absolute c-right-0 c-pointer-events-none`}
          >
            <BottomCurve size={isCollapsed ? 'SMALL' : 'BIG'} />
          </div>
        </>
      )}
      <tab.IconComponent className="c-w-5 c-h-5 c-shrink-0" />
      <div className={twMerge(isLabelHidden ? 'c-hidden' : 'c-ml-3', isSubItem && 'c-ml-2')}>{tab.label}</div>
    </LocalizedLink>
  )
}

/**
 * Returns the menuTabs having the longest `path` that matches the `currentPath`
 * The longest path is the most specific/relevant, it defines the menuItem that
 * is displayed as `active`
 *
 * @param {TMenuTab[]} menuTabs
 * @param {string} currentPath
 * @returns {TMenuTab | undefined}
 */
function getBestMatch(menuTabs: TMenuTab[], currentPath: string): TMenuTab | undefined {
  const matches = []
  // Testing each regex against the currentPath
  for (const menuTab of menuTabs) {
    const pathRegex = new RegExp(`^${menuTab.path}.*?$`, 'gi')
    if (pathRegex.test(currentPath)) {
      matches.push(menuTab)
    }
  }
  // Selecting the best (longest) match
  if (matches.length > 0) {
    const bestMatch = matches.reduce(function (a, b) {
      return a.path?.length > b.path?.length ? a : b
    })
    return bestMatch
  } else {
    return undefined
  }
}

const SidebarTabs = ({ menuTabs, isCollapsed = false }: { menuTabs: TMenuTab[]; isCollapsed: boolean }) => {
  const location = useLocation()
  const ulRef = useRef<HTMLUListElement>(null)

  // Remove locale prefix from pathname for matching (e.g., /fr/20/medical/dashboard -> /20/medical/dashboard)
  const pathnameWithoutLocale = location.pathname.replace(/^\/(fr|en)/, '')

  const allTabs = menuTabs.flatMap((tab) => [tab, ...(tab.subItems ?? [])])
  const activeLinkLabel = getBestMatch(allTabs, pathnameWithoutLocale)?.label

  return (
    <ul className="c-mt-4" ref={ulRef}>
      {menuTabs.map((tab) => {
        const hasSubItems = tab.subItems && tab.subItems.length > 0
        return (
          <WithFeatureEnabled featuresAccepted={tab.requiredFeature ?? []} key={tab.path}>
            <WithPermission
              requiredSubject={tab.requiredPermission?.subject}
              requiredAction={tab.requiredPermission?.action}
              key={tab.label}
            >
              <li className="c-relative c-mb-5 menu-tab-item">
                <TabContent
                  tab={tab}
                  isCollapsed={isCollapsed}
                  activeLinkLabel={activeLinkLabel}
                  isLabelHidden={isCollapsed}
                />

                {hasSubItems && !isCollapsed && (
                  <ul className="c-ml-3 c-text-[11px] c-mt-2">
                    {tab.subItems!.map((subItem) => {
                      return (
                        <WithFeatureEnabled
                          featuresAccepted={subItem.requiredFeature ?? []}
                          key={subItem.path}
                        >
                          <WithPermission
                            requiredSubject={subItem.requiredPermission?.subject}
                            requiredAction={subItem.requiredPermission?.action}
                            key={subItem.label}
                          >
                            <li className="c-relative c-mb-2 menu-tab-subitem">
                              <TabContent
                                tab={subItem}
                                isCollapsed={isCollapsed}
                                activeLinkLabel={activeLinkLabel}
                                isLabelHidden={false}
                                isSubItem={true}
                              />
                            </li>
                          </WithPermission>
                        </WithFeatureEnabled>
                      )
                    })}
                  </ul>
                )}
              </li>
            </WithPermission>
          </WithFeatureEnabled>
        )
      })}
    </ul>
  )
}

export default SidebarTabs
